import type { ReactNode } from "react";
import {
  hasNavigableTimeline,
  type Kind,
  type TickAction,
  TimelineBar,
  type VersionTick,
} from "@norkron/web-review-workbench";

/**
 * `<ResourcePane>` — the per-resource column shell (design §2.3): a
 * `position:relative` pane hosting an absolutely-positioned `<TimelineBar>` on the
 * LEFT edge (the web-review-workbench primitive) WHEN there are at least two
 * versions to scrub between, the `<ResourceHeader>` row, and
 * the pane body (`<DoczCanvas>` for brief/article, `<ResearchColumn>` for
 * research). Purely presentational — the parent supplies the header + body nodes
 * and the timeline data/handlers.
 */
export interface ResourcePaneProps {
  readonly kind: Kind;
  readonly versions: readonly VersionTick[];
  readonly selectedA?: string;
  readonly selectedB?: string;
  /** The version this pane is SHOWING — `undefined` means the live head. It is what
   *  gets `aria-current="true"` on the timeline (and therefore the pronounced tick);
   *  it is NOT `selectedA`/`selectedB`, which are the diff-compare pins. */
  readonly currentVersionId?: string;
  readonly onView?: (versionId: string) => void;
  readonly onSetA?: (versionId: string) => void;
  readonly onSetB?: (versionId: string) => void;
  /** Per-version actions on a tick's context menu (right-click, or Shift+F10 /
   *  the ContextMenu key on the focused tick). Passed straight through to the
   *  lib primitive, which owns both doors and the WAI-ARIA menu semantics. */
  readonly tickActions?: readonly TickAction[];
  readonly header: ReactNode;
  /** Edit-mode only: the formatting-toolbar dock band. Rendered as a second row of
   *  the sticky pane-header chrome (below `<ResourceHeader>`, ABOVE the paper), so
   *  the editor can portal its toolbar into header furniture instead of the paper. */
  readonly toolbarDock?: ReactNode;
  /** Optional bottom bar — a symmetric counterpart to `header`, rendered as the
   *  LAST child of `.wb-pane` (after `.wb-pane__body`). The workbench supplies it
   *  for brief/article panes (the word-count + export bar) and OMITS it for
   *  research (which gets no bottom bar — design §0.2). */
  readonly footer?: ReactNode;
  readonly children: ReactNode;
}

export function ResourcePane({
  kind,
  versions,
  selectedA,
  selectedB,
  currentVersionId,
  onView,
  onSetA,
  onSetB,
  tickActions,
  header,
  toolbarDock,
  footer,
  children,
}: ResourcePaneProps) {
  return (
    <div className="wb-pane" data-kind={kind} data-testid={`resource-pane-${kind}`}>
      {/* The timeline rail goes AS A WHOLE, wrapper included, once there is
          nothing to scrub between. `<TimelineBar>` already returns null below
          `hasNavigableTimeline`, so rendering the wrapper anyway would leave an
          orphan `.wb-timeline` positioning box around nothing. Gating on the
          lib's OWN predicate rather than a local `length > 1` is what keeps the
          two from drifting apart the day the threshold changes. */}
      {hasNavigableTimeline(versions) ? (
        <div className="wb-timeline">
          <TimelineBar
            versions={versions}
            selectedA={selectedA}
            selectedB={selectedB}
            currentVersionId={currentVersionId}
            onView={onView}
            onSetA={onSetA}
            onSetB={onSetB}
            tickActions={tickActions}
            testId={`timeline-${kind}`}
          />
        </div>
      ) : null}
      {/* The sticky pane-header chrome: the resource header row + (in edit mode) the
          formatting-toolbar dock band. Grouping them under ONE sticky wrapper keeps
          the toolbar pinned as header furniture above the paper — no magic offset. */}
      <div className="wb-pane__chrome">
        {header}
        {toolbarDock}
      </div>
      <div className="wb-pane__body">{children}</div>
      {footer}
    </div>
  );
}
